import { NETWORK_ERROR_MESSAGES, HTTP_STATUS_CODES } from '@shared/constants';

/**
 * @group Utils
 * @summary <blockquote>Error message mapping based on HTTP status codes.</blockquote>
 */

/**
 * @summary <blockquote>Error message mapping based on HTTP status codes.</blockquote>
 */
const ERROR_MESSAGES = {
  // Client errors (4xx)
  [HTTP_STATUS_CODES.BAD_REQUEST]: 'Invalid request. Please check your input and try again.',
  [HTTP_STATUS_CODES.UNAUTHORIZED]: 'Authentication required. Please sign in to continue.',
  [HTTP_STATUS_CODES.FORBIDDEN]: 'Access denied. You don\'t have permission to perform this action.',
  [HTTP_STATUS_CODES.NOT_FOUND]: 'Resource not found. The requested item doesn\'t exist.',
  [HTTP_STATUS_CODES.CONFLICT]: 'Conflict detected. The resource already exists or has been modified.',
  [HTTP_STATUS_CODES.UNPROCESSABLE_ENTITY]: 'Validation failed. Please check your input and try again.',
  [HTTP_STATUS_CODES.TOO_MANY_REQUESTS]: NETWORK_ERROR_MESSAGES.RATE_LIMITED,
  
  // Server errors (5xx)
  [HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR]: NETWORK_ERROR_MESSAGES.INTERNAL_ERROR,
  [HTTP_STATUS_CODES.BAD_GATEWAY]: NETWORK_ERROR_MESSAGES.SERVICE_UNAVAILABLE,
  [HTTP_STATUS_CODES.SERVICE_UNAVAILABLE]: NETWORK_ERROR_MESSAGES.SERVICE_UNAVAILABLE,
  [HTTP_STATUS_CODES.GATEWAY_TIMEOUT]: NETWORK_ERROR_MESSAGES.REQUEST_TIMEOUT,
  
  // Default messages
  NETWORK_ERROR: NETWORK_ERROR_MESSAGES.CONNECTION_FAILED,
  UNKNOWN_ERROR: NETWORK_ERROR_MESSAGES.UNKNOWN_ERROR,
  VALIDATION_ERROR: 'Please check your input and try again.',
  CONTEXT_ERROR: 'Component configuration error. Please refresh the page.',
} as const;

/**
 * @summary <blockquote>Determines the appropriate error message based on status code or error type.</blockquote>
 * 
 * @param statusCode - HTTP status code
 * @param errorMessage - Original error message (will be ignored for sensitive errors)
 * @returns Generic error message appropriate for user display
 */
export function getErrorMessage(statusCode?: number, errorMessage?: string): string {
  if (statusCode && ERROR_MESSAGES[statusCode as keyof typeof ERROR_MESSAGES]) {
    return ERROR_MESSAGES[statusCode as keyof typeof ERROR_MESSAGES];
  }
  
  // For network errors or unknown status codes
  if (!statusCode || statusCode >= HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR) {
    return ERROR_MESSAGES.UNKNOWN_ERROR;
  }
  
  // For client errors, we can be more specific but still generic
  if (statusCode >= HTTP_STATUS_CODES.BAD_REQUEST && statusCode < HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR) {
    return ERROR_MESSAGES.VALIDATION_ERROR;
  }
  
  return ERROR_MESSAGES.UNKNOWN_ERROR;
}
